import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { TableViewProps } from '@/type/listing.types';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { Select } from '@/components/Controls/Select';
import { getOptionsStatusFilterV2 } from '@/utils/constant.utils';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useRepairServiceFilter } from '@/hooks/filters/repair-service-filter.hooks';

const limit = 20;

export const RepairServiceTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    status,
    debounceFilterUpdate,
    searchByStatus,
  } = useRepairServiceFilter({ setCurrentPage });
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-1">
            <TableTitle text={t('common:repairServices')} />
          </div>
          <div className="flex gap-5">
            <Input
              placeholder={t('searchRepairService')}
              type="text"
              name="search"
              autoComplete="off"
              leftIcon={
                <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
              }
              inputWrapperClassName="w-[300px]"
              inputClassName="h-10"
              value={key}
              onChange={(e) => debounceFilterUpdate(e.target.value)}
            />
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-w-40"
              options={getOptionsStatusFilterV2(t)}
              value={status}
              onChange={(e) => searchByStatus(e.target.value)}
            />
            <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
          </div>
        </div>
        <div>
          <Datatable
            t={t}
            headers={<TableHeaders t={t} onSort={handleSort} />}
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
